

import { useEffect, useState } from "react";
import { DollarSign, ShieldCheck } from "lucide-react";
import { useRosterStore } from "@/store/roster-store";
import { getFirebaseServices } from "@/lib/firebase/client";
import { getAuth } from "firebase/auth";
import { doc, onSnapshot, setDoc, getDocs, collection } from 'firebase/firestore';
import { SkeletonStat } from "@/components/ui/Skeleton";

export function PayoutPanel() {
  const fallbackTotal = useRosterStore((state) => state.totalEarnings());
  const [paypalEmail, setPaypalEmail] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [available, setAvailable] = useState<number | null>(null);
  const [pending, setPending] = useState<number | null>(null);
  const [storedEmail, setStoredEmail] = useState<string>("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(true);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) return;
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const userRef = doc(services.db, `users/${uid}`);
    
    const unsubUser = onSnapshot(userRef, async (snap) => {
      if (snap.exists()) {
        const data = snap.data() as any;
        const email = data.paypalEmail || "";
        setStoredEmail(email);
        if (email) setPaypalEmail(email);
        
        // Get lastPayoutDate (default to 12/01/2025 if not set)
        let lastPayoutDate = data.lastPayoutDate;
        if (!lastPayoutDate) {
          // Initialize with default date
          lastPayoutDate = new Date('2025-12-01T00:00:00Z');
          await setDoc(userRef, { lastPayoutDate }, { merge: true });
        } else if (lastPayoutDate.toDate) {
          // Convert Firestore Timestamp to Date
          lastPayoutDate = lastPayoutDate.toDate();
        }
        
        // Calculate balance from events after lastPayoutDate
        try {
          const eventsRef = collection(services.db, `users/${uid}/installEvents`);
          const evSnap = await getDocs(eventsRef);
          let totalSinceLastPayout = 0;
          
          evSnap.forEach((d) => {
            const ev = d.data() as any;
            let eventDate: Date | null = null;
            
            // Parse event timestamp
            if (ev.timestamp && typeof ev.timestamp.toDate === 'function') {
              eventDate = ev.timestamp.toDate();
            } else if (typeof ev.timestamp === 'number') {
              eventDate = new Date(ev.timestamp);
            } else if (ev.storedAt && typeof ev.storedAt.toDate === 'function') {
              eventDate = ev.storedAt.toDate();
            }
            
            // Only count events after lastPayoutDate
            if (eventDate && eventDate > lastPayoutDate) {
              totalSinceLastPayout += Number(ev.cpi || 0) * Number(ev.eventCount || 0);
            }
          });
          
          setAvailable(totalSinceLastPayout);
          setPending(0); // Pending is now tracked separately if needed
          setLoadingBalance(false);
        } catch (err) {
          console.error('Failed to compute balance from lastPayoutDate', err);
          setLoadingBalance(false);
        }
      }
    });
    
    return () => {
      unsubUser();
    };
  }, []);

  const savePaypalEmail = async () => {
    if (!paypalEmail.trim()) {
      setStatus("Please enter a PayPal email.");
      return;
    }
    setSavingEmail(true);
    try {
      const services = getFirebaseServices();
      if (!services) throw new Error('Firebase not configured');
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Not authenticated');
      
      const userRef = doc(services.db, `users/${uid}`);
      await setDoc(userRef, { paypalEmail: paypalEmail.trim() }, { merge: true });
      setStatus("✅ PayPal email saved successfully!");
      setTimeout(() => setStatus(null), 3000);
    } catch (err: any) {
      console.error('Save PayPal error', err);
      setStatus(err.message || 'Failed to save PayPal email');
    } finally {
      setSavingEmail(false);
    }
  };

  const requestPayout = async () => {
    const amount = available ?? fallbackTotal;
    if (!amount || amount < 50) {
      setStatus("Minimum balance of $50 required for payouts.");
      return;
    }
    if (!paypalEmail) {
      setStatus("Add a PayPal email to continue.");
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const idToken = await user.getIdToken();

      const resp = await fetch('/api/request-payout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ amount, paypalEmail }),
      });
      const json = await resp.json();
      if (!json.success) throw new Error(json.error || 'Payout request failed');
      setStatus(`Payout request submitted. Finance will review the $${amount.toFixed(2)} balance.`);
    } catch (err: any) {
      console.error('Payout error', err);
      setStatus(err.message || 'Failed to request payout');
    }
  };

  const displayTotal = available ?? fallbackTotal;

  return (
    <section className="rounded-3xl border border-white/10 bg-zinc-900/70 p-5 text-white">
      <div className="mb-4 flex items-center gap-2 text-sm text-white/70">
        <ShieldCheck className="h-4 w-4" /> Secure payouts
      </div>
      <label className="text-xs uppercase text-white/60">PayPal email</label>
      <div className="mt-1 flex gap-2">
        <input
          value={paypalEmail}
          onChange={(event) => setPaypalEmail(event.target.value)}
          placeholder="creator@paypal.com"
          className="flex-1 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
        />
        <button
          onClick={savePaypalEmail}
          disabled={savingEmail || paypalEmail === storedEmail}
          className="rounded-2xl border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold transition hover:bg-white/20 disabled:opacity-50"
        >
          {savingEmail ? "Saving..." : "Add"}
        </button>
      </div>
      <div className="mt-3 flex flex-col gap-2 rounded-2xl border border-white/10 bg-black/30 p-3 text-sm">
        <p className="flex items-center gap-2">
          <DollarSign className="h-4 w-4" />
          Eligible balance
        </p>
        {loadingBalance ? (
          <SkeletonStat />
        ) : (
          <>
            <p className="text-3xl font-semibold text-lime-300">
              ${displayTotal.toFixed(2)}
            </p>
            {pending !== null && <p className="text-xs text-white/60">Pending: ${pending.toFixed(2)}</p>}
            <p className="text-xs text-white/60">Minimum $50 required before requesting payout.</p>
          </>
        )}
      </div>
      <button
        onClick={requestPayout}
        className="mt-4 w-full rounded-2xl bg-lime-400 px-4 py-3 text-center font-semibold text-black"
      >
        Request payout
      </button>
      {status && <p className="mt-2 text-sm text-white/70">{status}</p>}
    </section>
  );
}
